import { useState } from 'react';
import { ClipboardList, Printer, Send } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useDaySheet, useDraftDraw, usePublishDraw } from '@/api/extensions/useDrawOps';
import { JudgeSheetPrintable } from './JudgeSheetPrintable';
import { resolveJudgeSheetLayout } from '@/lib/judge-sheet-templates';
import { PermissionGate } from '@/components/rbac/PermissionGate';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import { useToast } from '@/contexts/ToastContext';

interface DaySheetViewProps {
  events: any[];
}

export function DaySheetView({ events }: DaySheetViewProps) {
  const [selectedEventId, setSelectedEventId] = useState(events[0]?.id ?? '');

  const { data: daySheet, isLoading } = useDaySheet(selectedEventId);
  const { data: draft } = useDraftDraw(selectedEventId);
  const publishDraw = usePublishDraw();
  const { showSuccess, showError } = useToast();

  if (events.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No events"
        description="No events available for day sheets."
      />
    );
  }

  // Determine data source — persisted (published) vs legacy (transient teams)
  const isPublishedDaySheet = (daySheet as any)?.source === 'published';
  const publishedEntries: any[] = (daySheet as any)?.entries ?? [];
  const teams: any[] = daySheet?.teams ?? [];
  const hasData = isPublishedDaySheet ? publishedEntries.length > 0 : teams.length > 0;

  // The /status endpoint never returns a `status: 'published'` field — the
  // authoritative draw lifecycle state lives on the draft execution's
  // `placement_status` ('draft' | 'published' | 'finalized'). `useDraftDraw`
  // falls back draft→finalized→published, so a published/finalized draw still
  // carries an execution_id; gate the Publish button strictly on the 'draft'
  // state so it disappears once the draw is published (and never no-ops by
  // re-publishing an already-published draw).
  const placementStatus = (draft as any)?.placement_status as string | undefined;
  const showPublishButton = placementStatus === 'draft';

  // Judge-sheet header fields are flattened onto the day-sheet response by the
  // draw-workflow endpoint. resolveJudgeSheetLayout walks the precedence:
  // inline columns from API → slug → discipline classifier fallback.
  const ds = daySheet as any;
  const { columns: judgeColumns, minRows: judgeMinRows } = resolveJudgeSheetLayout({
    columns: ds?.judge_sheet_columns,
    minRows: ds?.judge_sheet_min_rows,
    templateSlug: ds?.judge_sheet_template,
    eventTypeName: ds?.event_type_name,
  });
  const eventDateLabel = ds?.event_date
    ? new Date(ds.event_date).toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  // Per-performance judge sheets: one printed page per performance, contestants
  // in draw order (the API already returns entries sorted by draw_position).
  // Performance order/names come from the response's `performances` list; any
  // entry without a performance falls onto a trailing "Not Yet Drawn" sheet.
  const orderedPerformances: any[] = ds?.performances ?? [];

  const fmtPerfDateTime = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Team roping persists one entry per team with the heeler in
  // `partner_contestant`; the presence of any partner name flips the on-screen
  // day sheet into header/heeler column mode (solo disciplines stay single-col).
  const isTeamSheet = publishedEntries.some((e: any) => e.partner_contestant);

  const judgeSheetPerformances = (() => {
    const byPerf = new Map<string, { position: number; name: string; partnerName?: string }[]>();
    const unassigned: { position: number; name: string; partnerName?: string }[] = [];
    publishedEntries.forEach((e: any, idx: number) => {
      const contestant = {
        position: e.draw_position ?? idx + 1,
        name: e.contestant || '',
        // Team roping heeler (the backend supplies the header as `contestant`).
        partnerName: e.partner_contestant || undefined,
      };
      if (e.performance_id) {
        const list = byPerf.get(e.performance_id) ?? [];
        list.push(contestant);
        byPerf.set(e.performance_id, list);
      } else {
        unassigned.push(contestant);
      }
    });

    const out: { id: string; name: string; dateTime: string; contestants: { position: number; name: string; partnerName?: string }[] }[] = [];
    for (const p of orderedPerformances) {
      const list = byPerf.get(p.performance_id);
      if (!list || list.length === 0) continue;
      out.push({
        id: p.performance_id,
        name: `${p.performance_name ?? 'Performance'}${p.is_slack ? ' (Slack)' : ''}`,
        dateTime: fmtPerfDateTime(p.start_datetime),
        contestants: list,
      });
    }
    // Any performance_id present on entries but missing from the ordered list
    // (defensive — shouldn't happen) still gets a sheet.
    for (const [perfId, list] of byPerf) {
      if (!orderedPerformances.some((p) => p.performance_id === perfId)) {
        out.push({ id: perfId, name: 'Performance', dateTime: '', contestants: list });
      }
    }
    if (unassigned.length > 0) {
      out.push({ id: '__undrawn__', name: 'Not Yet Drawn', dateTime: '', contestants: unassigned });
    }
    return out;
  })();

  // On-screen day sheet, grouped by performance so it mirrors the printable
  // judge sheet. The flat list interleaved every performance by draw_position
  // (position 1 of perf 1, 2, 3 stacked together) and dropped the heeler — this
  // buckets entries into the same ordered performances + trailing "Not Yet
  // Drawn" group, carrying the full entry so header/heeler render.
  const daySheetPerformances = (() => {
    const byPerf = new Map<string, any[]>();
    const unassigned: any[] = [];
    publishedEntries.forEach((e: any) => {
      if (e.performance_id) {
        const list = byPerf.get(e.performance_id) ?? [];
        list.push(e);
        byPerf.set(e.performance_id, list);
      } else {
        unassigned.push(e);
      }
    });
    const out: { id: string; name: string; dateTime: string; entries: any[] }[] = [];
    for (const p of orderedPerformances) {
      const list = byPerf.get(p.performance_id);
      if (!list || list.length === 0) continue;
      out.push({
        id: p.performance_id,
        name: `${p.performance_name ?? 'Performance'}${p.is_slack ? ' (Slack)' : ''}`,
        dateTime: fmtPerfDateTime(p.start_datetime),
        entries: list,
      });
    }
    for (const [perfId, list] of byPerf) {
      if (!orderedPerformances.some((p) => p.performance_id === perfId)) {
        out.push({ id: perfId, name: 'Performance', dateTime: '', entries: list });
      }
    }
    if (unassigned.length > 0) {
      out.push({ id: '__undrawn__', name: 'Not Yet Drawn', dateTime: '', entries: unassigned });
    }
    return out;
  })();

  function handlePublish() {
    if (!selectedEventId) return;
    publishDraw.mutate(
      { eventId: selectedEventId },
      {
        onSuccess: () =>
          showSuccess(
            'Draw published',
            'The day sheet is now available to contestants.',
          ),
        onError: (err: any) =>
          showError('Publish failed', err?.message ?? 'Could not publish this draw.'),
      },
    );
  }

  function handlePrintJudgeSheet() {
    // CSS @media print swaps which DOM is visible; the browser dialog does the rest.
    window.print();
  }

  return (
    <div className="space-y-4">
      {/* Event selector + Publish button */}
      <div className="flex flex-wrap items-center gap-3">
        {events.length > 1 && (
          <Select value={selectedEventId} onValueChange={setSelectedEventId}>
            <SelectTrigger className="w-full sm:w-64 text-sm">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((ev: any) => (
                <SelectItem key={ev.id} value={ev.id}>
                  {getEventDisplayTitle(ev)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {showPublishButton && (
          <PermissionGate permission="draws:publish">
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publishDraw.isPending}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {publishDraw.isPending ? 'Publishing...' : 'Publish Draw'}
            </Button>
          </PermissionGate>
        )}

        {isPublishedDaySheet && publishedEntries.length > 0 && (
          <PermissionGate permission="draws:print_judge_sheet">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrintJudgeSheet}
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" />
              Print Judge Sheet
            </Button>
          </PermissionGate>
        )}
      </div>

      {/* Hidden-by-default printable. Made visible only under @media print. */}
      {isPublishedDaySheet && publishedEntries.length > 0 && (
        <JudgeSheetPrintable
          organizationName={ds?.organization_name ?? ''}
          rodeoName={ds?.rodeo_name ?? ''}
          roundLabel="1st Go Round"
          eventTypeName={ds?.event_type_name ?? ds?.event_name ?? ''}
          eventDate={eventDateLabel}
          columns={judgeColumns}
          minRows={judgeMinRows}
          performances={judgeSheetPerformances}
        />
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : !hasData ? (
        <EmptyState
          icon={ClipboardList}
          title="Draw not yet executed"
          description="The day sheet will be available after the draw is executed for this event."
        />
      ) : isPublishedDaySheet ? (
        /* ── Published day-sheet: persisted entries, grouped by performance ── */
        <div className="space-y-6">
          {daySheetPerformances.map((perf) => (
            <div key={perf.id} className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">{perf.name}</h3>
                {perf.dateTime && (
                  <span className="text-xs text-muted-foreground">{perf.dateTime}</span>
                )}
              </div>
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2 text-left font-medium">#</th>
                      {isTeamSheet ? (
                        <>
                          <th className="px-4 py-2 text-left font-medium">Header</th>
                          <th className="px-4 py-2 text-left font-medium">Heeler</th>
                        </>
                      ) : (
                        <th className="px-4 py-2 text-left font-medium">Contestant</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {perf.entries.map((entry: any, idx: number) => {
                      const isNonMember = !!entry.non_member;
                      return (
                        <tr
                          key={entry.registration_id ?? idx}
                          className="border-b last:border-b-0"
                        >
                          <td className="px-4 py-2 tabular-nums text-muted-foreground">
                            {entry.draw_position ?? idx + 1}
                          </td>
                          {isTeamSheet ? (
                            <>
                              <td className={`px-4 py-2 ${isNonMember ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                                {entry.contestant || '\u2014'}
                                {isNonMember && ' *'}
                              </td>
                              <td className="px-4 py-2">
                                {entry.partner_contestant || '\u2014'}
                              </td>
                            </>
                          ) : (
                            <td className={`px-4 py-2 ${isNonMember ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                              {entry.contestant || '\u2014'}
                              {isNonMember && ' *'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {publishedEntries.some((e: any) => e.non_member) && (
            <p className="text-xs text-muted-foreground">* Non-member entry</p>
          )}
        </div>
      ) : (
        /* ── Legacy transient teams view ── */
        <>
          <div className="overflow-x-auto rounded-md border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2 text-left font-medium">#</th>
                  <th className="px-4 py-2 text-left font-medium">Header</th>
                  <th className="px-4 py-2 text-left font-medium">Heeler</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team: any, idx: number) => {
                  const headerNonMember = !!team.header_non_member;
                  const heelerNonMember = !!team.heeler_non_member;
                  return (
                    <tr key={idx} className="border-b last:border-b-0">
                      <td className="px-4 py-2 tabular-nums text-muted-foreground">
                        {team.team_number ?? idx + 1}
                      </td>
                      <td className={`px-4 py-2 ${headerNonMember ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                        {team.header || '\u2014'}
                        {headerNonMember && ' *'}
                      </td>
                      <td className={`px-4 py-2 ${heelerNonMember ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                        {team.heeler || '\u2014'}
                        {heelerNonMember && ' *'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {teams.some((t: any) => t.header_non_member || t.heeler_non_member) && (
            <p className="text-xs text-muted-foreground">* Non-member entry</p>
          )}
        </>
      )}
    </div>
  );
}
